/* Presentation over the existing aveyra:favourites:v1 store; no second store. */
class AveyraWishlistDrawer extends HTMLElement {
  connectedCallback() {
    this.state = 'closed';
    this.version = 0;
    this.products = new Map();
    this.handles = this.read();
    this.content = this.querySelector('.aveyra-fav-content');
    document.querySelectorAll('.header__icon--wishlist').forEach(link => {
      link.setAttribute('role', 'button');
      link.setAttribute('aria-haspopup', 'dialog');
      link.setAttribute('aria-controls', 'AveyraFavTitle');
    });
    document.addEventListener('click', event => {
      const link = event.target.closest('.header__icon--wishlist');
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.open(link);
    });
    document.addEventListener('keydown', event => {
      if (event.key === 'Tab') this.querySelectorAll('[data-pointer-focus]').forEach(e => e.removeAttribute('data-pointer-focus'));
      if (event.code === 'Space' && event.target.closest('.header__icon--wishlist')) {
        event.preventDefault(); this.open(event.target.closest('.header__icon--wishlist'));
      }
    });
    document.addEventListener('aveyra:wishlist-change', event => {
      this.handles = event.detail;
      this.refresh();
    });
    this.addEventListener('click', event => {
      if (event.target.closest('[data-fav-close]')) this.close();
      const add = event.target.closest('[data-fav-add]');
      if (add) this.addToCart(add);
      if (event.target.closest('[data-fav-retry]')) { this.loadFailed = false; this.refresh(); }
    });
    this.addEventListener('pointerdown', event => {
      event.target.closest('.aveyra-fav-select')?.setAttribute('data-pointer-focus', '');
    });
    this.addEventListener('keydown', event => {
      event.target.closest('.aveyra-fav-select')?.removeAttribute('data-pointer-focus');
      if (event.key === 'Escape') { event.preventDefault(); this.close(); }
    });
    // Contain gestures locally; only the shared owner lock controls the page.
    this.addEventListener('touchstart', event => {
      if (event.touches.length === 1) this.touchY = event.touches[0].clientY;
    }, { passive: true });
    this.addEventListener('touchmove', event => {
      if (event.touches.length !== 1 || this.touchY === undefined) return;
      const dy = this.touchY - event.touches[0].clientY;
      this.touchY = event.touches[0].clientY;
      this.containGesture(event, dy);
    }, { passive: false });
    this.addEventListener('wheel', event => {
      this.containGesture(event, event.deltaY);
    }, { passive: false });
    this.addEventListener('change', event => {
      if (event.target.matches('select')) {
        const row = event.target.closest('.aveyra-fav-item');
        const product = this.products.get(row.dataset.handle);
        const variant = product.variants.find(v => String(v.id) === event.target.value);
        row.querySelector('[data-fav-color]').textContent = variant.title;
        row.querySelector('[data-fav-price]').textContent = this.money(variant.price);
        const image = variant.featured_image?.src;
        if (image) row.querySelector('img').src = image;
        row.querySelectorAll('a').forEach(link => {
          const url = new URL(link.href); url.searchParams.set('variant', variant.id); link.href = url.href;
        });
      }
    });
    this.onPageHide = () => this.finishClose(false);
    window.addEventListener('pagehide', this.onPageHide);
    this.refresh();
  }
  read() {
    try { const a = JSON.parse(localStorage.getItem('aveyra:favourites:v1') || '[]'); return Array.isArray(a) ? a.filter(x => typeof x === 'string' && !/[/?#\s]/.test(x)) : []; }
    catch (_) { return []; }
  }
  containGesture(event, dy) {
    if (!this.pageLock) return;
    let node = event.target instanceof Element ? event.target : event.target.parentElement;
    while (node && this.contains(node)) {
      if (/auto|scroll/.test(getComputedStyle(node).overflowY) && node.scrollHeight > node.clientHeight + 1 &&
          ((dy > 0 && node.scrollTop < node.scrollHeight - node.clientHeight - 1) || (dy < 0 && node.scrollTop > 0))) return;
      node = node.parentElement;
    }
    if (event.cancelable) event.preventDefault();
  }
  money(cents) { return new Intl.NumberFormat(document.documentElement.lang || 'it', { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(cents / 100); }
  async refresh() {
    const version = this.renderVersion = (this.renderVersion || 0) + 1;
    const restoreFocus = this.contains(document.activeElement);
    // Storage and heart counts update immediately; keep the removed row rendered
    // until its collapse completes so the following products reflow smoothly.
    const removals = [...this.content.querySelectorAll('[data-handle]')]
      .filter(row => !this.handles.includes(row.dataset.handle));
    await Promise.all(removals.map(row => this.removeRow(row)));
    await Promise.allSettled(this.handles.map(async handle => {
      if (this.products.has(handle)) return;
      const url = `${this.dataset.root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}.js`;
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Product unavailable');
      this.products.set(handle, await response.json());
    }));
    if (version !== this.renderVersion) return;
    this.render();
    if (removals.length && !this.handles.length && !this.hidden && !matchMedia('(prefers-reduced-motion: reduce)').matches)
      this.content.querySelector('.aveyra-fav-empty')?.animate(
        [{ opacity: 0, transform: 'translateY(14px)' }, { opacity: 1, transform: 'translateY(0)' }],
        { duration: 550, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'backwards' }
      );
    if (restoreFocus && !this.contains(document.activeElement) && this.state === 'open')
      this.querySelector('[data-fav-close][aria-label]').focus({ preventScroll: true });
    await Promise.allSettled([...this.content.querySelectorAll('img')].map(img => img.decode()));
  }
  removeRow(row) {
    this.rowRemovals ||= new WeakMap();
    if (this.rowRemovals.has(row)) return this.rowRemovals.get(row);
    const task = (async () => {
      row.querySelectorAll('button,select').forEach(e => { e.disabled = true; });
      if (!matchMedia('(prefers-reduced-motion: reduce)').matches && !this.hidden) {
        this.removingRows = (this.removingRows || 0) + 1;
        this.classList.add('is-removing');
        // Local confirmation phase matches the perceived cart request phase.
        // Wait for the actual indicator animation, never a cleanup timeout.
        const indicator = this.querySelector('.aveyra-fav-header').getAnimations({ subtree: true });
        await Promise.allSettled(indicator.map(animation => animation.finished));
        const rect = row.getBoundingClientRect();
        const style = getComputedStyle(row);
        const start = { height: `${rect.height}px`, paddingTop: style.paddingTop, paddingBottom: style.paddingBottom,
          marginTop: style.marginTop, marginBottom: style.marginBottom, borderBottomWidth: style.borderBottomWidth,
          opacity: 1, transform: 'translateY(0)' };
        const children = [...row.children].map(e => [e, e.getBoundingClientRect()]);
        children.forEach(([e, r]) => Object.assign(e.style, { position: 'absolute', left: `${r.left - rect.left}px`,
          top: `${r.top - rect.top}px`, width: `${r.width}px`, height: `${r.height}px` }));
        Object.assign(row.style, { position: 'relative', boxSizing: 'border-box', minHeight: '0', overflow: 'hidden', height: `${rect.height}px` });
        await row.animate([start, { height: '0px', paddingTop: '0px', paddingBottom: '0px', marginTop: '0px',
          marginBottom: '0px', borderBottomWidth: '0px', opacity: 0, transform: 'translateY(-10px)' }],
          { duration: 700, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'forwards' }).finished.catch(() => {});
        if (--this.removingRows === 0) this.classList.remove('is-removing');
      }
      row.remove();
    })();
    this.rowRemovals.set(row, task);
    return task;
  }
  render() {
    const selected = new Map([...this.content.querySelectorAll('[data-handle]')].map(row => [row.dataset.handle, row.querySelector('select')?.value]));
    const fragment = document.createDocumentFragment();
    const node = (tag, text, cls) => { const e = document.createElement(tag); if (text) e.textContent = text; if (cls) e.className = cls; return e; };
    if (!this.handles.length) fragment.append(node('p', 'La tua lista dei preferiti è vuota.', 'aveyra-fav-empty'));
    this.handles.forEach(handle => {
      const p = this.products.get(handle);
      const row = node('article', '', 'aveyra-fav-item'); row.dataset.handle = handle;
      if (!p) {
        row.append(node('p', 'Impossibile caricare questo prodotto.'));
        const retry = node('button', 'RIPROVA'); retry.dataset.favRetry = ''; row.append(retry);
      } else {
        const url = `${this.dataset.root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}`;
        const media = node('a'); media.href = url; media.tabIndex = -1; media.setAttribute('aria-hidden', 'true');
        if (p.featured_image) { const img = node('img'); img.src = p.featured_image; img.alt = p.title; img.width = 90; img.height = 120; media.append(img); }
        row.append(media);
        const info = node('div', '', 'aveyra-fav-info'); const title = node('a', p.title, 'aveyra-fav-name'); title.href = url; info.append(title);
        const variants = p.variants.filter(v => v.available);
        const chosen = variants.find(v => String(v.id) === selected.get(handle)) || variants[0];
        if (chosen) {
          const image = chosen.featured_image?.src;
          if (image && media.querySelector('img')) media.querySelector('img').src = image;
          media.href = title.href = `${url}?variant=${chosen.id}`;
        }
        const price = node('p', this.money(chosen?.price ?? p.price)); price.dataset.favPrice = ''; info.append(price);
        if (variants.length && !(p.variants.length === 1 && p.variants[0].title === 'Default Title')) {
          const label = node('label', 'Colore / variante', 'aveyra-fav-variant');
          const select = node('select'); select.setAttribute('aria-label', `Variante: ${p.title}`);
          variants.forEach(v => { const option = node('option', v.title); option.value = String(v.id); option.selected = v.id === chosen.id; select.append(option); });
          const control = node('span', '', 'aveyra-fav-select');
          const value = node('span', chosen.title); value.dataset.favColor = '';
          control.append(value, select); label.append(control); info.append(label);
        }
        const add = node('button', chosen ? 'AGGIUNGI AL CARRELLO' : 'ESAURITO', 'aveyra-fav-add');
        add.type = 'button'; add.dataset.favAdd = handle; add.dataset.variant = chosen?.id || ''; add.disabled = !chosen;
        const actions = node('div', '', 'aveyra-fav-actions'); actions.append(add); info.append(actions);
        row.append(info);
      }
      const remove = node('button', 'RIMUOVI', 'aveyra-fav-remove'); remove.type = 'button';
      remove.dataset.aveyraWishlist = handle; remove.dataset.productTitle = p?.title || handle;
      remove.dataset.labelAdd = 'Aggiungi ai preferiti'; remove.dataset.labelRemove = 'Rimuovi dai preferiti';
      remove.setAttribute('aria-label', `Rimuovi dai preferiti: ${p?.title || handle}`); remove.setAttribute('aria-pressed', 'true');
      (row.querySelector('.aveyra-fav-actions') || row).append(remove);
      fragment.append(row);
    });
    this.content.replaceChildren(fragment);
  }
  async open(trigger) {
    this.trigger = trigger;
    if (this.state === 'open' || this.state === 'opening') return;
    const version = ++this.version;
    this.state = 'loading';
    this.handles = this.read();
    trigger?.setAttribute('aria-busy', 'true');
    await this.refresh();
    trigger?.removeAttribute('aria-busy');
    if (version !== this.version) return;
    const cart = document.querySelector('cart-drawer');
    if (cart?.classList.contains('active') || cart?.pageLock) {
      cart.close();
      await new Promise(resolve => cart.afterSlide(() => requestAnimationFrame(resolve)));
    }
    if (version !== this.version) return;
    aveyraOverlayScrollLock.acquire(this);
    this.pageLock = true;
    document.querySelector('header-drawer')?.resetDrawer();
    this.hidden = false;
    this.state = 'opening';
    this.setAttribute('aria-hidden', 'false');
    document.querySelectorAll('.header__icon--wishlist').forEach(e => e.setAttribute('aria-expanded', 'true'));
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (version !== this.version) return;
      this.classList.add('is-open');
      this.afterMotion().then(() => {
        if (version !== this.version) return;
        this.state = 'open'; trapFocus(this.querySelector('section'), this.querySelector('[data-fav-close][aria-label]'));
      });
    }));
  }
  afterMotion() {
    return new Promise(resolve => requestAnimationFrame(() => Promise.allSettled([...this.querySelectorAll('.aveyra-fav-panel,.aveyra-fav-backdrop')].flatMap(e => e.getAnimations()).map(a => a.finished)).then(resolve)));
  }
  async close(restoreFocus = true) {
    const version = ++this.version;
    this.classList.remove('is-open'); this.state = 'closing';
    await this.afterMotion();
    if (version === this.version) this.finishClose(restoreFocus);
  }
  finishClose(restoreFocus) {
    ++this.version; this.state = 'closed'; this.hidden = true; this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    aveyraOverlayScrollLock.release(this); removeTrapFocus();
    this.pageLock = false;
    document.querySelectorAll('.header__icon--wishlist').forEach(e => e.setAttribute('aria-expanded', 'false'));
    if (restoreFocus) this.trigger?.focus({ preventScroll: true });
  }
  async addToCart(button) {
    if (button.disabled || this.addingToCart) return;
    this.addingToCart = true;
    const row = button.closest('[data-handle]');
    const id = row.querySelector('select')?.value || button.dataset.variant;
    const cart = document.querySelector('cart-drawer');
    button.disabled = true;
    let status = row.querySelector('[role="status"]');
    if (!status) { status = document.createElement('p'); status.setAttribute('role', 'status'); row.lastElementChild.append(status); }
    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id, quantity: 1, sections: cart?.getSectionsToRender().map(s => s.id) || [], sections_url: location.pathname })
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || 'Impossibile aggiungere il prodotto.');
      // Shopify may accept an add while failing its bundled section render.
      // Recover only the presentation; never repeat the successful POST.
      if (cart && (!data.sections?.['cart-drawer'] || !data.sections?.['cart-icon-bubble'])) {
        const url = new URL(location.href);
        url.searchParams.set('sections', cart.getSectionsToRender().map(s => s.id).join(','));
        const sectionsResponse = await fetch(url, { credentials: 'same-origin' });
        if (sectionsResponse.ok) data.sections = await sectionsResponse.json();
      }
      if (cart && !new DOMParser().parseFromString(data.sections?.['cart-drawer'] || '', 'text/html').querySelector('cart-drawer #CartDrawer'))
        throw new Error('Prodotto aggiunto. Impossibile aggiornare il carrello: apri il carrello dopo aver ricaricato la pagina.');
      await this.close(false);
      if (cart) { cart.setActiveElement(this.trigger); cart.renderContents(data); }
      else location.assign(window.routes.cart_url);
    } catch (error) { status.textContent = error.message; }
    finally { this.addingToCart = false; if (button.isConnected) button.disabled = false; }
  }
}
customElements.define('aveyra-wishlist-drawer', AveyraWishlistDrawer);
